import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { LISTING_STATUS } from '../../../../store/products/constants/listingStatus';
import {
  getChannelEntryHasLive,
  getChannelEntryListingStatus,
  getChannelEntryLiveUpdatedAt,
  getChannelEntryListingUpdatedAt,
  getListingProductStatusFromMap,
  hasLiveInProductListingsMap,
} from '../../../../store/selectors/productSelectors';
import SYNC_SCENARIO from '../constants/syncFlowConstants';
import {
  getVariantChannelEntry,
  isMasterVariantLiveOnChannel,
} from '../../../../store/products/helpers/listingSyncHelpers';

dayjs.extend(relativeTime);

export const getChannelLabels = (channelIds, channelOptions) => channelIds
  .map((channelId) => channelOptions.find((option) => option.value === channelId)?.label || channelId)
  .join(', ');

export const getListingStatusText = (listing) => {
  if (!listing) return 'Not set up yet';

  const hasLive = getChannelEntryHasLive(listing);
  const hasDraft = getChannelEntryListingStatus(listing) === LISTING_STATUS.DRAFT;

  if (hasLive && hasDraft) {
    const updatedAt = getChannelEntryListingUpdatedAt(listing);
    return updatedAt ? `Published | Draft updated: ${dayjs(updatedAt).fromNow()}` : 'Published | Draft';
  }

  if (hasLive) {
    const updatedAt = getChannelEntryLiveUpdatedAt(listing);
    return updatedAt ? `Last published: ${dayjs(updatedAt).fromNow()}` : 'Last published';
  }

  if (hasDraft) {
    const updatedAt = getChannelEntryListingUpdatedAt(listing);
    return updatedAt ? `Draft updated: ${dayjs(updatedAt).fromNow()}` : 'Draft';
  }

  return 'Not set up yet';
};

export const getSyncScenario = ({ variantId, product }) => {
  if (variantId) {
    return SYNC_SCENARIO.SINGLE_VARIANT;
  }

  if (product.variants?.length) {
    return SYNC_SCENARIO.CHOOSE_VARIANTS;
  }

  return SYNC_SCENARIO.PRODUCT;
};

/** Prefer entity-normalized listing variants, fall back to legacy product.channels. */
const resolveVariantChannelEntry = ({
  productsState,
  product,
  masterVariantId,
  channelId,
}) => (
  getVariantChannelEntry(productsState, masterVariantId, channelId)
  || getVariantChannelEntry(product, masterVariantId, channelId)
);

export const hasDraftListingForChannel = ({ channelId, productListings }) => (
  getListingProductStatusFromMap(productListings, channelId) === LISTING_STATUS.DRAFT
);

export const hasLiveListingForChannel = ({
  channelId,
  product,
  productsState,
  productListings,
  masterVariantIds,
}) => {
  if (!masterVariantIds?.length) {
    return hasLiveInProductListingsMap(productListings, channelId);
  }

  return masterVariantIds.some((masterVariantId) => (
    isMasterVariantLiveOnChannel({
      productListings,
      channelId,
      masterVariantId,
      listingVariant: resolveVariantChannelEntry({
        productsState,
        product,
        masterVariantId,
        channelId,
      }),
    })
  ));
};

export const getChannelsWithDraft = ({
  selectedChannelIds,
  productListings,
}) => selectedChannelIds.filter((channelId) => hasDraftListingForChannel({
  channelId,
  productListings,
}));

export const getChannelsWithLive = ({
  selectedChannelIds,
  product,
  productsState,
  productListings,
  masterVariantIds,
}) => selectedChannelIds.filter((channelId) => hasLiveListingForChannel({
  channelId,
  product,
  productsState,
  productListings,
  masterVariantIds,
}));
